import { ChangeDetectionStrategy, Component, ViewChild, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { FeatureFlagService } from '@trimble-gcs/feature-flags';
import { Feature } from 'geojson';
import { DialogData, OK_BUTTON } from '../dialog/dialog.model';
import { DialogService } from '../dialog/dialog.service';
import { ErrorState } from '../error-handling/error.state';
import { FeatureFlagKey } from '../feature-flags/feature-flag-key';
import { injectLogger } from '../logging/logger-injection';
import { ScandataEmptyReason } from '../scandata-list/scandata-empty/scandata-empty.component';
import { SortInfo } from '../scandata/scandata-query.models';
import {
  PatchScandataModel,
  SelectOnly,
  SelectScandataModel,
  SetSelected,
  SetSortInfo,
  UnselectScandataModel,
} from '../scandata/scandata.actions';
import { ScandataModel } from '../scandata/scandata.models';
import { ScandataState } from '../scandata/scandata.state';
import { SetActiveBaseLayer } from './base-layer/base-layer.actions';
import { BaseLayer } from './base-layer/base-layer.models';
import { BaseLayerService } from './base-layer/base-layer.service';
import { BaseLayerState } from './base-layer/base-layer.state';
import { FeatureLayerService } from './feature-layer/feature-layer.service';
import { FeatureLayerState } from './feature-layer/feature-layer.state';
import { MapListComponent } from './map-list/map-list.component';
import { MapToolbarComponent } from './map-toolbar/map-toolbar.component';
import {
  ClipRegionDrawnEvent,
  FeatureClickedEvent,
  MapViewerComponent,
} from './map-viewer/map-viewer.component';
import {
  ClearClipRegion,
  SetActiveMapTool,
  SetBounds,
  SetClipRegion,
  SetMapFilterOption,
} from './map.actions';
import { DEFAULT_BOUNDS_PADDING, MapBounds, MapFilterOption, MapTool } from './map.models';
import { MapService } from './map.service';
import { MapState } from './map.state';
import { mapTranslations } from './map.translations';
import { calculateBounds, findFeaturesIntersectingPolygon } from './map.util';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MapViewerComponent, MapListComponent, MapToolbarComponent],
  templateUrl: './map.component.html',
  styles: [],
})
export class MapComponent {
  private readonly logger = injectLogger('MapComponent');
  private readonly featureFlagService = inject(FeatureFlagService<FeatureFlagKey>);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);

  @ViewChild('mapViewer') private mapViewer!: MapViewerComponent;

  private readonly translations = mapTranslations();

  // loading
  private scandataLoading = this.store.selectSignal(ScandataState.isLoading);
  private featuresLoading = this.store.selectSignal(FeatureLayerState.isLoading);
  isLoading = computed(() => this.scandataLoading() || this.featuresLoading());
  scanLoadError = this.store.selectSignal(ErrorState.hasError('scanLoadError'));

  // sort
  sortInfo = this.store.selectSignal(ScandataState.sortInfo);

  // data
  mapScandata = toSignal(this.mapService.getMapScandata(), { initialValue: [] });

  // filter
  private filterCount = this.store.selectSignal(ScandataState.filterCount);
  private scandata = this.store.selectSignal(ScandataState.scandata);
  private textFilter = this.store.selectSignal(ScandataState.textFilter);
  filterOption = this.store.selectSignal(MapState.filterOption);

  // no data
  showScandataEmpty = computed(() => {
    return this.isLoading() || this.scanLoadError() ? false : this.mapScandata().length === 0;
  });

  scandataEmptyReason = this.getScandataEmptyReason();

  // layers
  baseLayers = toSignal(this.baseLayerService.getBaseLayers(), { initialValue: [] });
  featureLayer = toSignal(this.featureLayerService.getFeatureLayer(), { initialValue: null });
  activeBaseLayer = this.store.selectSignal(BaseLayerState.activeBaseLayer);

  bounds = this.store.selectSignal(MapState.bounds);
  activeMapTool = this.store.selectSignal(MapState.activeMapTool);
  clipRegion = this.store.selectSignal(MapState.clipRegion);

  readonly hasClipAndSaveFeature = toSignal(this.featureFlagService.hasFeature$('clipAndSave'), {
    initialValue: false,
  });

  readonly clipRegionPolygon = computed(() => this.clipRegion()?.polygon);

  constructor(
    private mapService: MapService,
    private featureLayerService: FeatureLayerService,
    private baseLayerService: BaseLayerService,
  ) {}

  featureClicked(event: FeatureClickedEvent) {
    const id = event.feature.id as string | undefined; //all our features have string ids;

    if (!id) {
      this.logger.error('Feature has no id', event.feature);
      return;
    }

    if (event.hasModifierKey) {
      if (event.feature.properties?.['selected']) {
        this.store.dispatch(new UnselectScandataModel(id));
      } else {
        this.store.dispatch(new SelectScandataModel(id));
      }
    } else {
      const selected = this.store.selectSnapshot(ScandataState.selected);
      if (selected.length === 1 && selected[0].id === id) {
        this.store.dispatch(new UnselectScandataModel(id));
      } else {
        this.store.dispatch(new SelectOnly([id]));
      }
    }
  }

  featureDblClicked(event: FeatureClickedEvent) {
    const feature = event.feature;
    const bounds: MapBounds = {
      bbox: calculateBounds(feature),
      padding: DEFAULT_BOUNDS_PADDING,
    };
    this.store.dispatch(new SetBounds(bounds));
  }

  featuresSelected(features: Feature[]) {
    const selectScanIds = this.mapScandata()
      .filter((scan) => !scan.hiddenOnMap)
      .filter((scan) => features.some((feature) => feature.id === scan.id))
      .map((scan) => scan.id);
    this.store.dispatch(new SelectOnly(selectScanIds));
  }

  clipRegionDrawn(event: ClipRegionDrawnEvent) {
    if (!this.hasClipAndSaveFeature()) return;

    const features = this.store.selectSnapshot(FeatureLayerState.featureCollection)?.features ?? [];
    const pointcloudIds = this.getVisibleIntersectingPointcloudIds(
      event.polygon,
      event.boundsWest,
      event.boundsEast,
      features,
    );

    if (pointcloudIds.length === 0) {
      this.showClipNoScansDialog();
      return;
    }

    this.store.dispatch(
      new SetClipRegion({
        polygon: event.polygon,
        pointcloudIds,
      }),
    );
  }

  clipRegionCleared() {
    this.store.dispatch(new ClearClipRegion());
  }

  changeItemVisibilityClicked(model: ScandataModel) {
    model.hiddenOnMap = !model.hiddenOnMap;
    this.store.dispatch(new PatchScandataModel(model));
  }

  selectionChange(scans: ScandataModel[]) {
    // Get the currently visible scans after applying the text filter
    const visibleScans = this.store.selectSnapshot(ScandataState.textFilteredScandata);

    // Identify selected scans that are currently filtered out (not visible)
    const hiddenSelectedScans = this.scandata()
      .filter((scan) => {
        const isHidden = !visibleScans.some((visibleScan) => visibleScan.id === scan.id);
        return isHidden && scan.selected;
      })
      .map((scan) => ({ ...scan, selected: false }));

    // Combine new selections with deselected hidden scans
    const updatedSelections = [...scans, ...hiddenSelectedScans];

    this.store.dispatch(new SetSelected(updatedSelections));
  }

  zoomToItemClicked(model: ScandataModel) {
    const featureCollection = this.store.selectSnapshot(FeatureLayerState.featureCollection);
    const feature = featureCollection?.features.find((ftr) => ftr.id === model.id);
    if (feature) {
      const bounds: MapBounds = {
        bbox: calculateBounds(feature),
        padding: DEFAULT_BOUNDS_PADDING,
      };
      this.store.dispatch(new SetBounds(bounds));
    }
  }

  mapToolSelected(mapTool: MapTool) {
    this.store.dispatch(new SetActiveMapTool(mapTool));
  }

  zoomIn() {
    this.mapViewer.zoomIn();
  }

  zoomOut() {
    this.mapViewer.zoomOut();
  }

  fitToView() {
    const featureCollection = this.store.selectSnapshot(FeatureLayerState.featureCollection);
    const bounds: MapBounds = {
      bbox: this.featureLayerService.getFeatureCollectionOrDefaultBBox(featureCollection),
      padding: DEFAULT_BOUNDS_PADDING,
    };

    this.store.dispatch(new SetBounds(bounds));
  }

  filterOptionChanged(option: MapFilterOption) {
    this.store.dispatch(new SetMapFilterOption(option));
  }

  sortChanged(sortInfo: SortInfo) {
    this.store.dispatch(new SetSortInfo(sortInfo));
  }

  setActiveBaseLayer(layer: BaseLayer) {
    this.store.dispatch(new SetActiveBaseLayer(layer));
  }

  boundsChanged(mapBounds: MapBounds) {
    this.store.dispatch(new SetBounds(mapBounds));
  }

  private showClipNoScansDialog() {
    const dialogData = new DialogData(
      this.translations.clipNoScans.title.text,
      this.translations.clipNoScans.message.text,
      OK_BUTTON,
    );
    this.dialogService.showMessage(dialogData);
  }

  private getVisibleIntersectingPointcloudIds(
    polygon: GeoJSON.Polygon,
    boundsWest: number,
    boundsEast: number,
    features: Feature[],
  ) {
    const visibleIds = new Set(
      this.mapScandata().filter((scan) => !scan.hiddenOnMap).map((scan) => scan.id),
    );

    return findFeaturesIntersectingPolygon(polygon, boundsWest, boundsEast, features)
      .map((feature) => feature.id as string)
      .filter((id) => visibleIds.has(id));
  }

  private getScandataEmptyReason() {
    return computed(() => {
      if (this.filterCount() === 0 && this.scandata().length === 0) {
        return ScandataEmptyReason.NoUploads;
      }

      if (this.filterCount() > 0 || isDefined(this.textFilter())) {
        return ScandataEmptyReason.NoFilterResults;
      }

      if (this.filterOption() === MapFilterOption.LocatedOnly) {
        return ScandataEmptyReason.NoGeolocated;
      }

      return ScandataEmptyReason.NoUploads;
    });
  }
}
